import * as React from 'react';
import { styled } from '@linaria/react';
import { SandpackPreview } from '@codesandbox/sandpack-react';

import { HEADER_HEIGHT } from '@/constants';

import Toolbar from './Toolbar';
import CodeEditor from './CodeEditor';
import ResultHeader from './ResultHeader';
import Console from './Console';
import SplitPane from './SplitPane';
import { Tab } from './Sandbox.types';

interface BaseUnitProps {
  title?: string;
  outputTabs: Array<Tab>;
  activeResultTab: Tab;
  handleSelectResultTab: (tab: Tab) => void;
  splitRatio: number;
  splitConsole: boolean;
  minPreviewHeight: number | string;
  transparentBackground: boolean;
  isRenderingPreview: boolean;
  maxEditorHeight?: number | string;
}

function UnitSplit({
  title,
  outputTabs,
  activeResultTab,
  handleSelectResultTab,
  splitConsole,
  splitRatio,
  minPreviewHeight,
  transparentBackground,
  isRenderingPreview,
  maxEditorHeight,
}: BaseUnitProps) {
  let secondColumn;

  // NOTE: I haven't tested split-console playgrounds that are side-by-side.
  if (splitConsole) {
    secondColumn = (
      <ResultOuterWrapper>
        <PreviewRow>
          <ResultHeader tabs={['result']} />
          <ResultWrapper
            style={{
              minHeight:
                typeof minPreviewHeight === 'number'
                  ? minPreviewHeight + 'px'
                  : minPreviewHeight,
            }}
          >
            <RoundedCorners>
              {isRenderingPreview && (
                <SandpackPreview
                  showNavigator={false}
                  showOpenInCodeSandbox={false}
                  showRefreshButton={false}
                />
              )}
            </RoundedCorners>
          </ResultWrapper>
        </PreviewRow>
        <ConsoleRow>
          <ResultHeader tabs={['console']} />
          <ResultWrapper
            style={{ paddingTop: 16, paddingBottom: 16 }}
          >
            <RoundedCorners>
              <ConsoleWrapper>
                <Console />
              </ConsoleWrapper>
            </RoundedCorners>
          </ResultWrapper>
        </ConsoleRow>
      </ResultOuterWrapper>
    );
  } else {
    secondColumn = (
      <ResultOuterWrapper>
        <ResultHeader
          tabs={outputTabs}
          activeTab={activeResultTab}
          handleSelectTab={handleSelectResultTab}
        />
        <ResultWrapper>
          <RoundedCorners
            style={{
              '--iframe-background-color': transparentBackground
                ? 'hsl(210deg 15% 6%)'
                : 'transparent',
            }}
          >
            {isRenderingPreview && (
              <SandpackPreview
                showNavigator={false}
                showOpenInCodeSandbox={false}
                showRefreshButton={false}
                style={{
                  minHeight:
                    typeof minPreviewHeight === 'number'
                      ? minPreviewHeight + 'px'
                      : minPreviewHeight,
                }}
              />
            )}

            <ConsoleWrapper
              style={{
                '--pointer-events':
                  activeResultTab === 'console' ? 'auto' : 'none',
                '--opacity': activeResultTab === 'console' ? 1 : 0,
                '--transition-delay':
                  activeResultTab === 'console' ? '0ms' : '100ms',
                '--child-delay':
                  activeResultTab === 'console' ? '200ms' : '0ms',
              }}
            >
              <Console />
            </ConsoleWrapper>
          </RoundedCorners>
        </ResultWrapper>
      </ResultOuterWrapper>
    );
  }

  const formattedMaxHeight =
    typeof maxEditorHeight === 'number'
      ? `${maxEditorHeight}px`
      : typeof maxEditorHeight === 'string'
        ? maxEditorHeight
        : `calc(100svh - ${HEADER_HEIGHT}rem - 140px)`;

  return (
    <InnerWrapper
      style={{ '--max-editor-height': formattedMaxHeight }}
    >
      <Toolbar title={title} />
      <SplitPane splitRatio={splitRatio}>
        <CodeEditor />
        {secondColumn}
      </SplitPane>
    </InnerWrapper>
  );
}

const InnerWrapper = styled.div`
  --color-primary: hsl(50deg 100% 50%);

  background: transparent;
  border: none;

  /*
    The following styles are only really needed in fullscreen,
    but they don't hurt here.
  */
  height: 100%;
  display: flex;
  flex-direction: column;

  /*
    We want to give the split unit a max height that fits comfortably within the viewport. It's a bit unusual, but the easiest way to do this seems to be to set the height on the Sandpack code editor itself.
  */
  .sp-code-editor {
    max-height: var(--max-editor-height);
  }
`;

const ResultOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0px;
  font-size: 0.875rem;
`;

const ResultWrapper = styled.div`
  flex: 1;
  position: relative;
  padding: 16px;
  /* Sandpack uses z-index, so we'll flatten it all here */
  isolation: isolate;
`;

const RoundedCorners = styled.div`
  border-radius: 4px;
  overflow: hidden;
  overflow: clip;
  height: 100%;

  iframe {
    background: var(--iframe-background-color);
  }
`;

const ConsoleWrapper = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  bottom: 15px;
  padding: 1px;
  /*
    This silly z-index number is required because sandpack uses
    a z-index of 3 for the overlay, instead of relying on the
    stacking order!!
  */
  z-index: 4;
  background: var(--syntax-bg);
  transition: opacity 200ms var(--transition-delay);
  opacity: var(--opacity);
  pointer-events: var(--pointer-events);

  & > * {
    opacity: var(--opacity);
    transition: opacity 200ms var(--child-delay);
  }
`;

const PreviewRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ConsoleRow = styled.div`
  height: 250px;
  display: flex;
  border-top: 1px solid var(--color-gray-100);
  flex-direction: column;
`;

export default UnitSplit;
